import { Component, inject, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButton } from '@angular/material/button';
import { SenderType } from '@vendasta/email';
import { createBlock$, deleteBlock$, getBlock$ } from '@vendasta/protos/email/v1/blocks';
import { EMPTY, Observable, of } from 'rxjs';
import { AsyncPipe, CommonModule } from '@angular/common';
import { catchError, switchMap } from 'rxjs/operators';
import { MatInput } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

interface Block {
  blockType: string;
  id: string;
  message: string;
  conditions: string;
}

const blockType = 'Sender';

@Component({
  selector: 'app-sender-status',
  templateUrl: './sender-status.component.html',
  styleUrls: ['./sender-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    MatCardModule,
    MatTabsModule,
    MatButton,
    AsyncPipe,
    MatInput,
    MatFormFieldModule,
    FormsModule,
  ],
})
export class SenderStatusComponent implements OnChanges {
  confirmationModal = inject(OpenConfirmationModalService);
  snack = inject(SnackbarService);

  @Input() senderId: string;
  @Input() senderType: SenderType;

  block$: Observable<Block | null>;
  inputtedBlockMessage = '';

  ngOnChanges() {
    this.block$ = getBlock$({ blockType, id: this.formatSenderID(this.senderType, this.senderId) }).pipe(
      catchError((_) => {
        this.inputtedBlockMessage = '';
        return of(null);
      }),
    );
  }

  formatSenderID(typ: SenderType, id: string): string {
    return `SENDER_TYPE:${this.valueFromSenderType(typ)},SENDER_ID:${id}`;
  }

  valueFromSenderType(senderType: SenderType): string {
    switch (senderType) {
      case SenderType.SENDER_TYPE_BUSINESS:
        return 'business';
      case SenderType.SENDER_TYPE_PARTNER:
        return 'partner';
      default:
        return '';
    }
  }

  blockSender() {
    this.confirmationModal
      .openModal({
        title: 'Are you sure you want to block this sender?',
        message: 'This sender will not be able to send any emails through our platform.',
        type: 'warn',
      })
      .pipe(
        switchMap((userDidAction) => {
          if (userDidAction === true) {
            return createBlock$({
              blockType,
              message: this.inputtedBlockMessage,
              conditions: `{"sender_id": "${this.senderId}","sender_type": ${this.senderType}}`,
            });
          }
          return of(EMPTY);
        }),
      )
      .subscribe((r) => {
        if (r != EMPTY) {
          this.snack.openSuccessSnack('Sender blocked successfully.');
        }
      });
  }

  unblockSender() {
    this.confirmationModal
      .openModal({
        title: 'Are you sure you want to unblock this sender?',
        message: 'This sender will be able to send emails through our platform.',
        type: 'confirm',
      })
      .pipe(
        switchMap((userDidAction) => {
          if (userDidAction === true) {
            return deleteBlock$({ blockType, id: this.formatSenderID(this.senderType, this.senderId) });
          }
          return of(EMPTY);
        }),
      )
      .subscribe((r) => {
        if (r != EMPTY) {
          this.snack.openSuccessSnack('Sender unblocked successfully.');
        }
      });
  }
}
